import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import type { EventCategoryDto, PublishImportAsFeedCategorySlug, SourceMessageDto } from '@upme/contracts';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@upme/ui/components/alert-dialog';
import { Button } from '@upme/ui/components/button';
import { Ban, RefreshCw, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { buildImportQueueFeedPublishActions } from '../config/feed-publish-actions';
import type { ImportQueueT } from '../lib/format';
import { navigateToNextImportItem } from '../lib/navigate-next-import-item';
import {
  useIgnoreImportItem,
  usePublishImportAsFeed,
  useReextractImportItem,
  useRejectImportItem,
} from '../mutations';
import type { PublishFromImportFormApi } from './publish-from-import-form';

interface Props {
  item: SourceMessageDto;
  categories: ReadonlyArray<EventCategoryDto>;
  publishApi: PublishFromImportFormApi | null;
}

export function ImportQueueModerationActions({ item, categories, publishApi }: Props) {
  const { t } = useTranslation('import-queue');
  const router = useRouter({ warn: false });
  const qc = useQueryClient();

  const reextract = useReextractImportItem(item.id);
  const ignore = useIgnoreImportItem(item.id);
  const reject = useRejectImportItem(item.id);
  const publishAsFeed = usePublishImportAsFeed(item.id);

  const [rejectNote, setRejectNote] = useState('');
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);

  const feedPublishActions = useMemo(() => buildImportQueueFeedPublishActions(t as ImportQueueT), [t]);

  const goNext = async () => {
    await navigateToNextImportItem(qc, router, item.id, { status: 'EXTRACTED' });
  };

  const handleReextract = async () => {
    try {
      await reextract.mutateAsync();
      toast.success(t('moderation.reextractSuccess'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('moderation.reextractError'));
    }
  };

  const handleIgnore = async () => {
    try {
      await ignore.mutateAsync();
      toast.success(t('moderation.ignoreSuccess'));
      await goNext();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('moderation.ignoreError'));
    }
  };

  const handleReject = async () => {
    try {
      const note = rejectNote.trim();
      await reject.mutateAsync(note ? { note } : {});
      toast.success(t('moderation.rejectSuccess'));
      setRejectNote('');
      await goNext();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('moderation.rejectError'));
    }
  };

  const handleFeedAction = async (kind: 'event' | 'news', slug: string) => {
    if (!publishApi) {
      toast.error(t('moderation.formNotReady'));
      return;
    }
    setPendingSlug(slug);
    try {
      if (kind === 'event') {
        const result = await publishApi.submitEvent(categories);
        if (result === 'duplicate') {
          // Найден дубль — форма показывает диалог force-override; очередь не продвигаем.
          return;
        }
        if (result === 'invalid') {
          toast.error(t('moderation.invalidFormFields'));
          return;
        }
        toast.success(t('moderation.eventPublished'));
      } else {
        const feedFields = publishApi.getFeedFields();
        await publishAsFeed.mutateAsync({
          feedCategorySlug: slug as PublishImportAsFeedCategorySlug,
          ...feedFields,
        });
        const action = feedPublishActions.find((a) => a.slug === slug);
        toast.success(
          action
            ? t('moderation.publishedToFeed', { label: action.label })
            : t('moderation.publishedToFeedGeneric'),
        );
      }
      await goNext();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('moderation.publishFailed'));
    } finally {
      setPendingSlug(null);
    }
  };

  const isBusy =
    reextract.isPending ||
    ignore.isPending ||
    reject.isPending ||
    publishAsFeed.isPending ||
    pendingSlug !== null;

  return (
    <div className="space-y-4 pt-2 border-t">
      <Button
        type="button"
        className="w-full sm:w-auto"
        disabled={isBusy || !publishApi}
        onClick={() => void handleFeedAction('event', 'events')}
      >
        {pendingSlug === 'events' ? t('moderation.publishingEllipsis') : t('moderation.createEvent')}
      </Button>

      <div className="flex flex-wrap gap-2">
        <Button onClick={handleReextract} disabled={isBusy} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          {t('moderation.reextract')}
        </Button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm" disabled={isBusy}>
              <Ban className="h-4 w-4 mr-2" />
              {t('moderation.ignore')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('moderation.ignoreConfirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription>{t('moderation.ignoreConfirmDescription')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('moderation.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleIgnore} disabled={ignore.isPending}>
                {ignore.isPending ? t('moderation.loadingEllipsis') : t('moderation.ignoreConfirmAction')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" size="sm" disabled={isBusy}>
              <XCircle className="h-4 w-4 mr-2" />
              {t('moderation.reject')}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('moderation.rejectConfirmTitle')}</AlertDialogTitle>
              <AlertDialogDescription>{t('moderation.rejectConfirmDescription')}</AlertDialogDescription>
            </AlertDialogHeader>
            <div>
              <textarea
                rows={3}
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder={t('moderation.rejectNotePlaceholder')}
                className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setRejectNote('')}>
                {t('moderation.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleReject} disabled={reject.isPending}>
                {reject.isPending ? t('moderation.rejectingEllipsis') : t('moderation.reject')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="space-y-2">
        <div className="text-sm font-medium">{t('moderation.otherSectionLabel')}</div>
        <div className="flex flex-wrap gap-2">
          {feedPublishActions
            .filter((action) => action.kind === 'news')
            .map((action) => (
              <Button
                key={action.slug}
                type="button"
                variant="outline"
                size="sm"
                disabled={isBusy || !publishApi}
                className={action.buttonClassName}
                onClick={() => void handleFeedAction(action.kind, action.slug)}
              >
                {pendingSlug === action.slug ? t('moderation.publishingEllipsis') : action.label}
              </Button>
            ))}
        </div>
      </div>
    </div>
  );
}
